import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { GitBranch, Loader2, ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, History, Undo2, Clock } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function _timeAgo(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 48) return `${h}h ago`;
    const days = Math.floor(h / 24); return `${days}d ago`;
  } catch { return ''; }
}

/**
 * Cross-Vendor Duplicate Detector.
 *
 * Sweeps the product catalog for likely duplicates that live under
 * different vendors (e.g. "Agilysis" vs "Agilysys"). Presents each
 * group with a preview and a one-click Merge control.
 *
 * Merge History drawer surfaces recent merges with a grace-period
 * Undo button, so admins can confidently roll back a bad merge.
 */
export default function CrossVendorDupScanner({ token }) {
  const [groups, setGroups] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [primaryOverride, setPrimaryOverride] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [graceHours, setGraceHours] = useState(24);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const scan = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/cross-vendor-dupes`, { headers, params: { min_ratio: 0.9 } });
      setGroups(res.data.groups || []);
      setMeta({ total: res.data.total_groups, scanned: res.data.scanned_products });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    } finally {
      setBusy(false);
    }
  };

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/admin/cross-vendor-dupes/history`, { headers, params: { limit: 50 } });
      setHistory(res.data.entries || []);
      setGraceHours(res.data.grace_hours || 24);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'History load failed');
    } finally {
      setHistoryLoading(false);
    }
  }, [headers]);

  useEffect(() => { if (showHistory) fetchHistory(); }, [showHistory, fetchHistory]);

  const merge = async (group, gi) => {
    const primary_id = primaryOverride[gi] || group.primary_id;
    const dup_ids = group.products.map(p => p.id).filter(id => id !== primary_id);
    const primaryName = group.products.find(p => p.id === primary_id)?.name || 'primary';
    if (!window.confirm(`Merge ${dup_ids.length} duplicates into "${primaryName}"?\nAssignments will be reassigned to the primary and the duplicate products will be deleted.\n\nThis action can be undone from the Merge History drawer for the next ${graceHours} hours.`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND}/api/admin/cross-vendor-dupes/merge`, { primary_id, dup_ids }, { headers });
      toast.success(`Merged into "${primaryName}" — ${res.data.reassigned_assignments} assignment${res.data.reassigned_assignments !== 1 ? 's' : ''} reassigned${res.data.dropped_duplicate_assignments ? `, ${res.data.dropped_duplicate_assignments} duplicates dropped` : ''}`);
      // Remove this group from the list
      setGroups(prev => (prev || []).filter((_, i) => i !== gi));
      // Refresh history if it's open
      if (showHistory) fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setBusy(false);
    }
  };

  const undoMerge = async (entry) => {
    if (!window.confirm(`Undo merge of "${entry.primary_name}"?\nThis will restore ${entry.dup_names?.length || 0} duplicate product${entry.dup_names?.length === 1 ? '' : 's'} and revert all reassigned assignments.`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND}/api/admin/cross-vendor-dupes/undo/${entry.id}`, {}, { headers });
      toast.success(`Restored ${res.data.restored_products} product${res.data.restored_products !== 1 ? 's' : ''} · ${res.data.reverted_assignments} assignment${res.data.reverted_assignments !== 1 ? 's' : ''} reverted${res.data.restored_assignments ? ` · ${res.data.restored_assignments} restored` : ''}`);
      fetchHistory();
      // Force rescan on next open to pick up restored dupes
      setGroups(null);
      setMeta(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Undo failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-5 max-w-4xl space-y-4" data-testid="cross-vendor-dup-scanner">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <GitBranch className="h-5 w-5 text-violet-500" />
              Cross-Vendor Duplicate Detector
            </h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-lg">
              Finds products that look like the same underlying tool but are attached to different vendors — a common data-hygiene issue after catalog imports. Review each group, choose the record to keep, and merge in one click. Every merge is logged with a grace-period Undo.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowHistory(v => !v)} className="gap-1.5" data-testid="dup-history-toggle">
              <History className="h-3.5 w-3.5" />
              {showHistory ? 'Hide history' : 'Merge history'}
            </Button>
            <Button size="sm" onClick={scan} disabled={busy} className="gap-1.5" data-testid="dup-scan-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitBranch className="h-3.5 w-3.5" />}
              {groups == null ? 'Run scan' : 'Rescan'}
            </Button>
          </div>
        </div>

        {/* Merge History Drawer */}
        {showHistory && (
          <div className="rounded-xl border bg-card overflow-hidden" data-testid="dup-history-panel">
            <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-violet-600" />
                <div>
                  <div className="text-sm font-semibold">Merge History</div>
                  <div className="text-[10px] text-muted-foreground">Merges within the last {graceHours}h are highlighted as safely undoable</div>
                </div>
              </div>
              {historyLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            </div>
            <div className="max-h-[320px] overflow-y-auto divide-y">
              {(!history || history.length === 0) && !historyLoading && (
                <div className="p-8 text-center text-xs text-muted-foreground" data-testid="dup-history-empty">
                  No merges recorded yet. Once you merge duplicates they'll appear here with an Undo control.
                </div>
              )}
              {(history || []).map(entry => (
                <div key={entry.id} className={`p-3 flex items-start gap-3 text-xs ${entry.is_within_grace ? '' : 'opacity-70'}`} data-testid={`dup-history-entry-${entry.id}`}>
                  <div className={`h-6 w-6 rounded-full grid place-items-center shrink-0 ${entry.undone_at ? 'bg-slate-200 text-slate-500' : entry.is_within_grace ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {entry.undone_at ? <Undo2 className="h-3 w-3" /> : <GitBranch className="h-3 w-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate flex items-center gap-1.5">
                      <span>{entry.primary_name || entry.primary_id}</span>
                      {entry.undone_at && <span className="text-[9px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Undone</span>}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                      Absorbed {entry.dup_names?.length || 0} duplicate{(entry.dup_names?.length || 0) !== 1 ? 's' : ''}: {(entry.dup_names || []).join(', ') || '—'}
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-2.5 w-2.5" /> {_timeAgo(entry.created_at)}</span>
                      <span>·</span>
                      <span>{entry.counts?.reassigned_assignments || 0} reassigned</span>
                      {entry.counts?.dropped_duplicate_assignments > 0 && (
                        <>
                          <span>·</span>
                          <span>{entry.counts.dropped_duplicate_assignments} dropped</span>
                        </>
                      )}
                      {entry.actor_email && (
                        <>
                          <span>·</span>
                          <span className="truncate max-w-[160px]">by {entry.actor_email}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {!entry.undone_at && (
                    <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={() => undoMerge(entry)} disabled={busy} data-testid={`dup-history-undo-${entry.id}`}>
                      <Undo2 className="h-3 w-3" /> Undo
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {meta && (
          <div className="rounded-lg border p-3 bg-muted/30 text-xs text-muted-foreground">
            Scanned <strong className="text-foreground">{meta.scanned}</strong> products · found <strong className="text-foreground">{meta.total}</strong> likely-duplicate group{meta.total !== 1 ? 's' : ''}
          </div>
        )}

        {groups && groups.length === 0 && (
          <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="dup-scan-clean">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-3" />
            No cross-vendor duplicates detected. Your catalog is clean.
          </div>
        )}

        {groups && groups.length > 0 && (
          <div className="space-y-3">
            {groups.map((g, gi) => {
              const isOpen = expanded[gi] !== false;
              const primaryId = primaryOverride[gi] || g.primary_id;
              return (
                <div key={gi} className="rounded-xl border bg-card overflow-hidden" data-testid={`dup-group-${gi}`}>
                  <button
                    onClick={() => setExpanded(p => ({ ...p, [gi]: !isOpen }))}
                    className="w-full flex items-center justify-between p-3 hover:bg-muted/30 text-left"
                  >
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <div>
                        <div className="text-sm font-semibold">{g.products[0].name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {g.products.length} products across {new Set(g.products.map(p => p.vendor_id)).size} vendors
                        </div>
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-violet-600 font-bold">Review</span>
                  </button>
                  {isOpen && (
                    <div className="border-t bg-muted/10 p-3 space-y-2">
                      {g.products.map(p => {
                        const isPrimary = p.id === primaryId;
                        return (
                          <div key={p.id} className={`flex items-center gap-2 rounded-md border p-2 ${isPrimary ? 'border-violet-300 bg-violet-50/60' : 'bg-card'}`}>
                            <button
                              onClick={() => setPrimaryOverride(o => ({ ...o, [gi]: p.id }))}
                              className={`inline-flex items-center justify-center h-4 w-4 rounded-full border shrink-0 ${isPrimary ? 'border-violet-500 bg-violet-500' : 'border-muted-foreground/40'}`}
                              title="Choose as primary (kept after merge)"
                              data-testid={`dup-primary-${gi}-${p.id}`}
                            >
                              {isPrimary && <span className="h-1.5 w-1.5 bg-white rounded-full" />}
                            </button>
                            <div className="flex-1">
                              <div className="text-sm font-medium">{p.name}</div>
                              <div className="text-[10px] text-muted-foreground">
                                Vendor: <strong>{p.vendor_name}</strong> · {p.assignment_count} assignment{p.assignment_count !== 1 ? 's' : ''} · Platforms: {p.platform_ids.join(', ') || '—'}
                              </div>
                            </div>
                            {isPrimary && <span className="text-[9px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">Primary</span>}
                          </div>
                        );
                      })}
                      <div className="flex justify-end pt-1">
                        <Button size="sm" onClick={() => merge(g, gi)} disabled={busy} className="h-8 gap-1.5" data-testid={`dup-merge-${gi}`}>
                          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <GitBranch className="h-3 w-3" />}
                          Merge into primary
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {groups == null && !busy && (
          <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
            Click <b>Run scan</b> to sweep the product catalog for likely cross-vendor duplicates.
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
